import { observer } from "mobx-react-lite";
import { useIntl } from "react-intl";
import { NutritionProductPlan } from "../../services/PlansService";
import { useContext } from "react";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import { AppContext } from "../../contexts/AppContext";
import { NutritionProduct } from "../../services/NutritionProductsService";
import styled from "@mui/material/styles/styled";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import posthog from "posthog-js";

const ParagraphLine = styled("span")(({ theme }) => ({
    verticalAlign: "baseline",
    height: 1,
    flexGrow: 1,
    flexShrink: 1,
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    marginLeft: "0.5em",
    marginRight: "0.5em"
}));

const SmallTextField = styled(TextField)(({ theme }) => ({
    maxWidth: theme.spacing(6),
    minWidth: theme.spacing(6),
    "& input": {
        fontSize: "0.875rem"
    }
}));

export interface NutritionProductsListProps {
    nutrition: NutritionProductPlan[];
    setNutrition: (nutrition: NutritionProductPlan[]) => void;
    showAddFavorite?: boolean;
    onOpenProductsTable?: () => void;
    readOnly?: boolean;
}

const NutritionProductsList: React.FC<NutritionProductsListProps> = observer(({
    nutrition,
    setNutrition,
    showAddFavorite = true,
    onOpenProductsTable,
    readOnly
}) => {

    const intl = useIntl();
    const { nutritionProductsService } = useContext(AppContext);

    const usedNutritionProducts = nutrition.map((item) => item.productId);

    const addNutrition = () => {
        posthog.capture('nutrition_add_product');
        setNutrition([...nutrition, { id: crypto.randomUUID(), productId: "empty", quantity: 0 }]);
    }

    const onChangeProduct = (productId: string, index: number) => {
        posthog.capture('nutrition_product_selected', { productId });
        if (productId === "empty") {
            return;
        }
        if (productId === "add" && onOpenProductsTable) {
            onOpenProductsTable();
        } else {
            const newNutrition = [...nutrition];
            newNutrition[index].productId = productId;
            setNutrition(newNutrition);
        }
    }

    const onChangeQuantity = (productId: string, quantity: number) => {
        posthog.capture('nutrition_product_quantity_changed', { productId, quantity });
        const newNutrition = [...nutrition];
        const nutritionProductIndex = newNutrition.findIndex((item) => item.productId === productId);
        if (nutritionProductIndex > -1) {
            newNutrition[nutritionProductIndex].quantity = quantity;
            setNutrition(newNutrition);
        }
    }

    const deleteProduct = (nutritionProductId: string) => {
        posthog.capture('nutrition_product_deleted', { productId: nutritionProductId });
        const newNutrition = nutrition.filter((item) => item.productId !== nutritionProductId);
        setNutrition(newNutrition);
    }

    if (readOnly) {
        return (
            <>
                {nutrition.map((nutritionItem) => {
                    const product = nutritionItem.productId === "water"
                        ? { name: intl.formatMessage({ id: "data.waterLiters" }) }
                        : nutritionProductsService.nutritionProducts.find(p => p.id === nutritionItem.productId);
                    if (!product) return null;
                    return (
                        <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1} key={nutritionItem.id ?? nutritionItem.productId}>
                            <Typography variant="body2">{product.name}</Typography>
                            <ParagraphLine />
                            <Typography variant="body2">{nutritionItem.quantity}</Typography>
                        </Box>
                    );
                })}
            </>
        );
    }

    return (
        <>
            {nutrition.map((nutritionItem, index) => (
                <Box display="flex" alignItems="baseline" justifyContent="space-between" marginY={1} key={nutritionItem.id ?? nutritionItem.productId}>
                    <Box display="flex" alignItems="baseline">
                        <Select
                            value={nutritionItem.productId}
                            label="Product"
                            size="small"
                            variant="standard"
                            onChange={(event) => onChangeProduct(event.target.value as string, index)}
                            style={{ fontSize: "0.875rem" }}
                        >
                            <MenuItem value="empty"><b>{intl.formatMessage({ id: "plans.chooseProduct" })}</b></MenuItem>
                            {(nutritionItem.productId === "water" || !usedNutritionProducts.includes("water")) &&
                                <MenuItem value="water">{intl.formatMessage({ id: "data.waterLiters" })}</MenuItem>}
                            {nutritionProductsService.nutritionProducts.map((product: NutritionProduct) => (
                                !!product.id && (nutritionItem.productId === product.id || !usedNutritionProducts.includes(product.id)) ?
                                    <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem> : null
                            ))}
                            {showAddFavorite && <MenuItem value="add"><b>{intl.formatMessage({ id: "plans.addFavoriteProduct" })}</b></MenuItem>}
                        </Select>
                    </Box>
                    <ParagraphLine />
                    <Box display="flex" alignItems="baseline">
                        <SmallTextField
                            hiddenLabel
                            variant="standard"
                            size="small"
                            type="number"
                            value={nutritionItem.quantity}
                            onChange={(ev) => onChangeQuantity(nutritionItem.productId, +ev.target.value)}
                            InputProps={{ inputProps: { step: 'any' } }}
                        />
                        <IconButton onClick={() => deleteProduct(nutritionItem.productId)} size="small">
                            <Delete fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            ))}
            <Box display="flex" alignItems="baseline" justifyContent="end" marginY={1}>
                <Button
                    startIcon={<Add />}
                    size="small"
                    onClick={addNutrition}
                    disabled={nutritionProductsService.nutritionProducts.length + 1 <= usedNutritionProducts.length ||
                        !!nutrition.find(sN => sN.productId === "empty")}
                >
                    {intl.formatMessage({ id: "plans.addNutrition" })}
                </Button>
            </Box>
        </>
    );
});

export default NutritionProductsList;
